import React, { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  FiUser, FiPackage, FiMapPin, FiLock, FiHeart,
  FiLogOut, FiChevronRight, FiEdit2, FiTrash2, FiPlus,
  FiCheck, FiAlertCircle, FiEye, FiEyeOff, FiStar,
  FiShoppingCart, FiArrowRight, FiChevronDown, FiChevronUp,
  FiClock, FiTruck, FiX, FiHome, FiLogIn, FiUserPlus,
  FiChevronLeft
} from "react-icons/fi";
import { useCustomer, useCart } from "../context/ShopContext";
import ShopTopBar from "../components/ShopTopBar";
import Navbar from "../components/Navbar";
import "./Account.css";

const fmt = (n) =>
  "R " + Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_META = {
  pending:    { label: "Pending",    cls: "acc-status--pending"    },
  confirmed:  { label: "Confirmed",  cls: "acc-status--confirmed"  },
  processing: { label: "Processing", cls: "acc-status--processing" },
  shipped:    { label: "Shipped",    cls: "acc-status--shipped"    },
  delivered:  { label: "Delivered",  cls: "acc-status--delivered"  },
  cancelled:  { label: "Cancelled",  cls: "acc-status--cancelled"  },
};

const PAY_META = {
  unpaid: { label: "Unpaid", cls: "acc-pay--unpaid" },
  paid:   { label: "Paid",   cls: "acc-pay--paid"   },
};

/* ══════════════════════════════════════
   SIDE NAV
   ══════════════════════════════════════ */
const NAV_ITEMS = [
  { key: "orders",   label: "Order History",   icon: <FiPackage size={17} /> },
  { key: "address",  label: "Address Book",    icon: <FiMapPin  size={17} /> },
  { key: "wishlist", label: "Wishlist",         icon: <FiHeart   size={17} /> },
  { key: "profile",  label: "My Profile",      icon: <FiUser    size={17} /> },
  { key: "password", label: "Change Password", icon: <FiLock    size={17} /> },
];

function SideNav({ active, customer, onLogout }) {
  return (
    <aside className="acc-sidenav">
      <div className="acc-sidenav__profile">
        <div className="acc-sidenav__avatar">
          {customer?.first_name?.[0]}{customer?.last_name?.[0]}
        </div>
        <div className="acc-sidenav__name">
          <strong>{customer?.first_name} {customer?.last_name}</strong>
          <span>{customer?.email}</span>
        </div>
      </div>
      <nav className="acc-sidenav__nav">
        {NAV_ITEMS.map(item => (
          <Link
            key={item.key}
            to={`/account/${item.key}`}
            className={`acc-sidenav__item ${active === item.key ? "acc-sidenav__item--active" : ""}`}
          >
            {item.icon}
            <span>{item.label}</span>
            <FiChevronRight size={14} className="acc-sidenav__arrow" />
          </Link>
        ))}
      </nav>
      <div className="acc-sidenav__footer">
        <Link to="/shop" className="acc-sidenav__shop-link">
          <FiShoppingCart size={15} /> Continue Shopping
        </Link>
        <button className="acc-sidenav__logout" onClick={onLogout}>
          <FiLogOut size={15} /> Sign Out
        </button>
      </div>
    </aside>
  );
}

/* ══════════════════════════════════════
   ORDERS TAB
   ══════════════════════════════════════ */
function OrdersTab({ token }) {
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId,  setOpenId]  = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <TabSkeleton rows={4} />;

  if (orders.length === 0) return (
    <div className="acc-empty">
      <FiPackage size={48} />
      <h3>No orders yet</h3>
      <p>Once you place an order, it'll appear here.</p>
      <Link to="/shop" className="acc-btn acc-btn--primary">Browse Shop <FiArrowRight size={14} /></Link>
    </div>
  );

  return (
    <div className="acc-orders">
      {orders.map(order => {
        const sm = STATUS_META[order.status] || { label: order.status, cls: "" };
        const pm = PAY_META[order.payment_status] || { label: order.payment_status, cls: "" };
        const open = openId === order.id;
        return (
          <div key={order.id} className={`acc-order-card ${open ? "acc-order-card--open" : ""}`}>
            <div className="acc-order-card__head" onClick={() => setOpenId(open ? null : order.id)}>
              <div className="acc-order-card__num">
                <span className="acc-order-card__label">Order</span>
                <strong>#{order.order_number}</strong>
              </div>
              <div className="acc-order-card__meta">
                <span className={`acc-status ${sm.cls}`}>{sm.label}</span>
                <span className={`acc-pay ${pm.cls}`}>{pm.label}</span>
                <span className="acc-order-card__date">
                  <FiClock size={12} />{" "}
                  {new Date(order.created_at).toLocaleDateString("en-ZA", {
                    day: "numeric", month: "short", year: "numeric",
                  })}
                </span>
                <span className="acc-order-card__total">{fmt(order.total)}</span>
              </div>
              <button className="acc-order-card__toggle">
                {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              </button>
            </div>

            {open && (
              <div className="acc-order-card__body">
                {order.items && order.items.length > 0 && (
                  <table className="acc-order-items">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map(item => (
                        <tr key={item.id}>
                          <td className="acc-order-items__name">{item.product_name}</td>
                          <td className="acc-order-items__sku">{item.sku || "—"}</td>
                          <td>{item.quantity}</td>
                          <td>{fmt(item.unit_price)}</td>
                          <td className="acc-order-items__total">{fmt(item.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="acc-order-card__summary">
                  <div className="acc-order-card__sum-row">
                    <span>Subtotal</span><span>{fmt(order.subtotal)}</span>
                  </div>
                  {Number(order.discount_amount) > 0 && (
                    <div className="acc-order-card__sum-row acc-order-card__sum-row--disc">
                      <span>Discount</span><span>− {fmt(order.discount_amount)}</span>
                    </div>
                  )}
                  {Number(order.shipping_cost) > 0 && (
                    <div className="acc-order-card__sum-row">
                      <span>Shipping</span><span>{fmt(order.shipping_cost)}</span>
                    </div>
                  )}
                  <div className="acc-order-card__sum-row acc-order-card__sum-row--total">
                    <span>Total</span><span>{fmt(order.total)}</span>
                  </div>
                </div>
                {order.shipping_address && (
                  <div className="acc-order-card__addr">
                    <FiTruck size={13} /> <span>Ship to: {order.shipping_address}</span>
                  </div>
                )}
                {order.payment_method && (
                  <div className="acc-order-card__addr">
                    <FiCheck size={13} /> <span>Payment: {order.payment_method}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════
   ADDRESS TAB
   ══════════════════════════════════════ */
function AddressTab({ customer, token, onCustomerUpdate }) {
  const PROVINCES = [
    "Gauteng","Western Cape","KwaZulu-Natal","Eastern Cape",
    "Limpopo","Mpumalanga","North West","Free State","Northern Cape",
  ];
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");
  const [form, setForm] = useState({
    address:     customer?.address     || "",
    city:        customer?.city        || "",
    province:    customer?.province    || "",
    postal_code: customer?.postal_code || "",
  });

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/address`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Failed to save.");
      onCustomerUpdate({ ...customer, ...form });
      setSuccess(true); setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const hasAddress = customer?.address || customer?.city;

  return (
    <div className="acc-address-tab">
      {success && (
        <div className="acc-alert acc-alert--success"><FiCheck size={15} /> Address saved successfully.</div>
      )}
      {error && (
        <div className="acc-alert acc-alert--error"><FiAlertCircle size={15} /> {error}</div>
      )}

      {!editing ? (
        <div className="acc-address-card">
          <div className="acc-address-card__header">
            <div className="acc-address-card__title">
              <FiHome size={16} /> Default Delivery Address
              <span className="acc-address-card__badge">Primary</span>
            </div>
            <button className="acc-btn acc-btn--ghost" onClick={() => setEditing(true)}>
              <FiEdit2 size={14} /> Edit
            </button>
          </div>
          {hasAddress ? (
            <div className="acc-address-card__body">
              <p>{customer.address}</p>
              <p>{[customer.city, customer.province, customer.postal_code].filter(Boolean).join(", ")}</p>
              <p>South Africa</p>
            </div>
          ) : (
            <div className="acc-address-card__empty">
              <FiMapPin size={28} />
              <p>No address saved yet.</p>
              <button className="acc-btn acc-btn--primary" onClick={() => setEditing(true)}>
                <FiPlus size={14} /> Add Address
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="acc-address-form">
          <h3>Edit Delivery Address</h3>
          <div className="acc-form-grid">
            <div className="acc-form__group acc-form__group--full">
              <label>Street Address</label>
              <input
                value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main Street"
              />
            </div>
            <div className="acc-form__group">
              <label>City</label>
              <input
                value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                placeholder="Johannesburg"
              />
            </div>
            <div className="acc-form__group">
              <label>Province</label>
              <select
                value={form.province}
                onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
              >
                <option value="">Select…</option>
                {PROVINCES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="acc-form__group">
              <label>Postal Code</label>
              <input
                value={form.postal_code}
                onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                placeholder="2000"
              />
            </div>
          </div>
          <div className="acc-form__actions">
            <button className="acc-btn acc-btn--ghost" onClick={() => { setEditing(false); setError(""); }}>
              Cancel
            </button>
            <button className="acc-btn acc-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><FiCheck size={14} /> Save Address</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   WISHLIST TAB
   ══════════════════════════════════════ */
function WishlistTab({ token }) {
  const { addToCart, cart } = useCart();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const remove = async (productId) => {
    await fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/wishlist/${productId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    setItems(items => items.filter(i => i.product_id !== productId));
  };

  if (loading) return <TabSkeleton rows={3} />;

  if (items.length === 0) return (
    <div className="acc-empty">
      <FiHeart size={48} />
      <h3>Your wishlist is empty</h3>
      <p>Save products you love and find them here later.</p>
      <Link to="/shop" className="acc-btn acc-btn--primary">Browse Shop <FiArrowRight size={14} /></Link>
    </div>
  );

  return (
    <div className="acc-wishlist">
      {items.map(item => {
        const inCart = cart.some(c => c.id === item.product_id);
        const price  = Number(item.sale_price || item.price);
        const old    = item.sale_price ? Number(item.price) : null;
        return (
          <div key={item.product_id} className="acc-wish-card">
            <div className="acc-wish-card__img">
              {item.image1
                ? <img src={`${process.env.REACT_APP_API_URL}/uploads/shop/${item.image1}`} alt={item.name} />
                : <div className="acc-wish-card__no-img"><FiPackage size={28} /></div>
              }
            </div>
            <div className="acc-wish-card__info">
              <Link to={`/shop/${item.slug}`} className="acc-wish-card__name">{item.name}</Link>
              {item.car_make && <p className="acc-wish-card__fit">{item.car_make} {item.car_model}</p>}
              <div className="acc-wish-card__prices">
                <span className="acc-wish-card__price">{fmt(price)}</span>
                {old && <span className="acc-wish-card__old">{fmt(old)}</span>}
              </div>
            </div>
            <div className="acc-wish-card__actions">
              <button
                className={`acc-btn acc-btn--primary acc-btn--sm ${inCart ? "acc-btn--done" : ""}`}
                onClick={() => addToCart(item, 1)}
                disabled={item.quantity < 1}
              >
                <FiShoppingCart size={14} />
                {item.quantity < 1 ? "Out of Stock" : inCart ? "In Cart" : "Add to Cart"}
              </button>
              <button className="acc-wish-card__remove" onClick={() => remove(item.product_id)} title="Remove">
                <FiTrash2 size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════
   PROFILE TAB
   ══════════════════════════════════════ */
function ProfileTab({ customer, token, onCustomerUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");
  const [form, setForm] = useState({
    first_name: customer?.first_name || "",
    last_name:  customer?.last_name  || "",
    email:      customer?.email      || "",
    phone:      customer?.phone      || "",
  });

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/profile`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Failed to save.");
      onCustomerUpdate({ ...customer, ...form });
      setSuccess(true); setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="acc-profile-tab">
      {success && <div className="acc-alert acc-alert--success"><FiCheck size={15} /> Profile updated.</div>}
      {error   && <div className="acc-alert acc-alert--error"><FiAlertCircle size={15} /> {error}</div>}

      {!editing ? (
        <div className="acc-profile-view">
          <div className="acc-profile-view__avatar">
            {customer?.first_name?.[0]}{customer?.last_name?.[0]}
          </div>
          <div className="acc-profile-view__details">
            <div className="acc-profile-row">
              <span>Full Name</span>
              <strong>{customer?.first_name} {customer?.last_name}</strong>
            </div>
            <div className="acc-profile-row">
              <span>Email</span>
              <strong>{customer?.email}</strong>
            </div>
            <div className="acc-profile-row">
              <span>Phone</span>
              <strong>{customer?.phone || "—"}</strong>
            </div>
            <div className="acc-profile-row">
              <span>Member Since</span>
              <strong>
                {new Date(customer?.created_at).toLocaleDateString("en-ZA", {
                  month: "long", year: "numeric",
                })}
              </strong>
            </div>
          </div>
          <button className="acc-btn acc-btn--ghost" onClick={() => setEditing(true)}>
            <FiEdit2 size={14} /> Edit Profile
          </button>
        </div>
      ) : (
        <div className="acc-address-form">
          <h3>Edit Profile</h3>
          <div className="acc-form-grid">
            <div className="acc-form__group">
              <label>First Name *</label>
              <input required value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="acc-form__group">
              <label>Last Name *</label>
              <input required value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div className="acc-form__group">
              <label>Email *</label>
              <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="acc-form__group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
          <div className="acc-form__actions">
            <button className="acc-btn acc-btn--ghost" onClick={() => { setEditing(false); setError(""); }}>
              Cancel
            </button>
            <button className="acc-btn acc-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : <><FiCheck size={14} /> Save Changes</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   PASSWORD TAB
   ══════════════════════════════════════ */
function PasswordTab({ token }) {
  const [form, setForm]       = useState({ current_password: "", password: "", password2: "" });
  const [showPw, setShowPw]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  const handleSave = async () => {
    if (form.password !== form.password2) { setError("New passwords do not match."); return; }
    if (form.password.length < 8)         { setError("Password must be at least 8 characters."); return; }
    setSaving(true); setError("");
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/me/password`, {
        method:  "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ current_password: form.current_password, new_password: form.password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Failed.");
      setSuccess(true);
      setForm({ current_password: "", password: "", password2: "" });
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="acc-password-tab">
      {success && <div className="acc-alert acc-alert--success"><FiCheck size={15} /> Password changed successfully.</div>}
      {error   && <div className="acc-alert acc-alert--error"><FiAlertCircle size={15} /> {error}</div>}

      <div className="acc-pw-info">
        <FiLock size={18} />
        <p>Choose a strong password with at least 8 characters.</p>
      </div>

      <div className="acc-address-form">
        <div className="acc-form-grid acc-form-grid--single">
          <div className="acc-form__group">
            <label>Current Password</label>
            <div className="acc-pw-wrap">
              <input
                type={showPw ? "text" : "password"}
                value={form.current_password}
                onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))}
                placeholder="Your current password"
              />
              <button type="button" className="acc-pw-toggle" onClick={() => setShowPw(v => !v)}>
                {showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}
              </button>
            </div>
          </div>
          <div className="acc-form__group">
            <label>New Password</label>
            <div className="acc-pw-wrap">
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min. 8 characters"
              />
            </div>
          </div>
          <div className="acc-form__group">
            <label>Confirm New Password</label>
            <div className="acc-pw-wrap">
              <input
                type={showPw ? "text" : "password"}
                value={form.password2}
                onChange={e => setForm(f => ({ ...f, password2: e.target.value }))}
                placeholder="Re-enter new password"
              />
            </div>
          </div>
        </div>
        <div className="acc-form__actions">
          <button
            className="acc-btn acc-btn--primary"
            onClick={handleSave}
            disabled={saving || !form.current_password || !form.password}
          >
            {saving ? "Changing…" : <><FiLock size={14} /> Change Password</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   SKELETON LOADER
   ══════════════════════════════════════ */
function TabSkeleton({ rows = 3 }) {
  return (
    <div className="acc-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="acc-skeleton__row" style={{ animationDelay: `${i * 0.06}s` }} />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN ACCOUNT PAGE
   ══════════════════════════════════════ */
export default function AccountPage() {
  const { section = "orders" } = useParams();
  const navigate = useNavigate();
  const { customer, token, logout, login } = useCustomer();
  const [localCustomer, setLocalCustomer] = useState(customer);

  useEffect(() => { setLocalCustomer(customer); }, [customer]);

  useEffect(() => {
    if (!customer) navigate("/login");
  }, [customer]);

  if (!customer) return null;

  const handleLogout = () => { logout(); navigate("/shop"); };

  const handleCustomerUpdate = (updated) => {
    setLocalCustomer(updated);
    login(updated, token);
  };

  const SECTION_TITLES = {
    orders:   "Order History",
    address:  "Address Book",
    wishlist: "Wishlist",
    profile:  "My Profile",
    password: "Change Password",
  };

  return (
    <main className="acc-page">

      {/* ── Main site Navbar — identical to Shop.jsx ── */}
      <Navbar />

      {/* ── Hero banner ── */}
      <div className="acc-hero">
        <div className="acc-hero__bg-image" />
        <div className="acc-hero__overlay" />

        <div className="container acc-hero__inner">
          <div className="acc-hero__left">
            <div className="acc-hero__tag">MY ACCOUNT</div>
            <h1 className="acc-hero__title">
              WELCOME BACK,<br /><span>{localCustomer?.first_name?.toUpperCase()}</span>
            </h1>
            {/* Breadcrumb inside hero content */}
            <div className="acc-hero__crumb">
              <Link to="/">Home</Link>
              <FiArrowRight size={12} />
              <Link to="/shop">Shop</Link>
              <FiArrowRight size={12} />
              <span>{SECTION_TITLES[section] || "Account"}</span>
            </div>
          </div>
        </div>

        {/* ── Corner mini-topbar — identical pattern to Shop.jsx ── */}
        <div className="acc-hero__mini-topbar">
          <ShopTopBar />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="container acc-body">

        {/* Mobile nav select */}
        <div className="acc-mobile-nav">
          <select value={section} onChange={e => navigate(`/account/${e.target.value}`)}>
            {NAV_ITEMS.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
          </select>
        </div>

        <SideNav active={section} customer={localCustomer} onLogout={handleLogout} />

        <div className="acc-content">
          <div className="acc-content__header">
            <h2>{SECTION_TITLES[section] || "Account"}</h2>
          </div>

          <div className="acc-content__body">
            {section === "orders"   && <OrdersTab   token={token} />}
            {section === "address"  && <AddressTab  customer={localCustomer} token={token} onCustomerUpdate={handleCustomerUpdate} />}
            {section === "wishlist" && <WishlistTab token={token} />}
            {section === "profile"  && <ProfileTab  customer={localCustomer} token={token} onCustomerUpdate={handleCustomerUpdate} />}
            {section === "password" && <PasswordTab token={token} />}
          </div>
        </div>
      </div>
    </main>
  );
}